import type {CSSProperties, MouseEvent as ReactMouseEvent, ReactElement} from 'react';
import {useSortable} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {ClickableDiv} from '@Components/clickable-div';
import {Checkbox} from '@Components/checkbox';
import {DRAG_PLACEHOLDER_DATA_ATTRIBUTE, GROUP_ICON, GROUP_LABEL_KEY} from '../../layers-panel.types';
import {isEventFromSelectionCheckbox, toGroupSortableId} from '../../layers-panel.helper';
import {useGroupThumbDataURL} from '@Hooks/poster-editor/useGroupThumbDataURL';
import styles from './layer-group-row.module.scss';
import panelStyles from '../../layers-panel.module.scss';

interface LayerGroupRowProps {
  groupId: string;
  isExpanded: boolean;
  isSelected: boolean;
  isOverlay?: boolean;
  disableDrag?: boolean;
  hideStackBars?: boolean;
  showSelectionCheckbox?: boolean;
  onClick?: (groupId: string, event: ReactMouseEvent<HTMLDivElement>) => void;
  onCheckboxClick?: (groupId: string) => void;
}

export function LayerGroupRow({
  groupId,
  isExpanded,
  isSelected,
  isOverlay = false,
  disableDrag = false,
  hideStackBars = false,
  showSelectionCheckbox = false,
  onClick,
  onCheckboxClick,
}: LayerGroupRowProps): ReactElement {
  const sortableId = toGroupSortableId(groupId);
  const {attributes, isDragging, listeners, setNodeRef, transform, transition} = useSortable({id: sortableId, disabled: disableDrag});
  const thumbSrc = useGroupThumbDataURL(groupId);

  const showDropPlaceholder = isDragging && !isOverlay;
  const hideOverlayClone = isDragging && isOverlay;

  const style: CSSProperties = {
    ...(hideOverlayClone && {opacity: 0}),
    transform: CSS.Translate.toString(transform),
    transition,
  };

  const onClickRow = (event: ReactMouseEvent<HTMLDivElement>): void => {
    if (isEventFromSelectionCheckbox(event)) {
      return;
    }
    onClick?.(groupId, event);
  };

  const onClickCheckbox = (): void => {
    onCheckboxClick?.(groupId);
  };

  const rowClasses = `${styles.row} _position-relative flexbox flex-items-center gap-2 spacing-p-2 ${isSelected ? styles.rowSelected : ''}`;
  const containerClasses = `${styles.container} _cursor-pointer flex-v-row ${showDropPlaceholder ? panelStyles.dragging : ''}`;

  const renderPreview = (): ReactElement => {
    if (thumbSrc) {
      return <img alt="" className={styles.thumb} draggable={false} src={thumbSrc} />;
    }
    return <Icon icon={GROUP_ICON} size={IconSize.SIZE_ICON_16} className={`${styles.icon} flex-shrink-0`} />;
  };

  const renderStackBars = (): ReactElement | null => {
    if (isExpanded || hideStackBars) {
      return null;
    }
    return (
      <>
        <div className={`${styles.stackBar} ${styles.stackBar1} spacing-m-t-1 spacing-m-l-2 spacing-m-r-2`} />
        <div className={`${styles.stackBar} ${styles.stackBar2} spacing-m-t-1 spacing-m-l-4 spacing-m-r-4`} />
      </>
    );
  };

  const renderDropPlaceholder = (): ReactElement | null => {
    if (!showDropPlaceholder) {
      return null;
    }
    return <div className={panelStyles.dropPlaceholder} />;
  };

  const renderDragHandle = (): ReactElement => {
    return <Icon icon="icon-draggable" size={IconSize.SIZE_ICON_16} className={`${styles.dragHandle} flex-shrink-0`} />;
  };

  const renderTrailingSlot = (): ReactElement | null => {
    if (showSelectionCheckbox) {
      return <Checkbox isChecked={isSelected} onClick={onClickCheckbox} className={panelStyles.selectionCheckbox} />;
    }
    return null;
  };

  return (
    <ClickableDiv
      ref={setNodeRef}
      className={containerClasses}
      style={style}
      enableFocusOutline={false}
      onClick={onClickRow}
      aria-label={window.i18next.t(GROUP_LABEL_KEY)}
      aria-expanded={isExpanded}
      {...(showDropPlaceholder ? {[DRAG_PLACEHOLDER_DATA_ATTRIBUTE]: ''} : {})}
      {...attributes}
      {...listeners}
    >
      <div className={rowClasses}>
        {renderDropPlaceholder()}
        <div className={`${styles.handleSlot} flex-center flex-shrink-0`}>{renderDragHandle()}</div>
        <div className={`${styles.thumbSlot} flex-center flex-1`}>{renderPreview()}</div>
        <div className={`${styles.trailingSlot} flex-center flex-shrink-0`}>{renderTrailingSlot()}</div>
      </div>
      {renderStackBars()}
    </ClickableDiv>
  );
}
